import BaseApiService from '../../auditor/service/baseApiService.mjs';

/**
 * Signing a monthly report: the saved signature, and signing on a phone.
 *
 * The signature itself travels with the action it signs (send to auditor,
 * approve) - see SignatureModal for its shape. The server decides who may sign
 * what (backend src/signatures/stageSignature.mjs); this only carries requests.
 */
const toError = (error, fallback) => {
  const e = new Error(error.response?.data?.message || fallback);
  e.status = error.response?.status;
  e.code = error.response?.data?.errorCode;
  return e;
};

class SignatureService extends BaseApiService {
  /** The signer's saved signature: { method, typedName, image, updatedAt } or null. */
  async getSaved() {
    try {
      const response = await this.api.get('/signatures/saved');
      return response.data?.data || null;
    } catch (error) {
      throw toError(error, 'Could not load your saved signature');
    }
  }

  /** Forget the saved signature. Reports already signed keep their copy. */
  async deleteSaved() {
    try {
      await this.api.delete('/signatures/saved');
    } catch (error) {
      throw toError(error, 'Could not remove your saved signature');
    }
  }

  /** Open a phone handoff: { id, token, code, path, expiresAt }. */
  async openHandoff(reportId) {
    try {
      const response = await this.api.post('/signatures/handoffs', { reportId });
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Could not start signing on your phone');
    }
  }

  /** The desktop's poll: { status: pending|captured|consumed|expired, image }. */
  async getHandoff(id) {
    try {
      const response = await this.api.get(`/signatures/handoffs/${encodeURIComponent(id)}`);
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Could not check your phone signature');
    }
  }

  // ---- the phone, not signed in

  /** What the phone shows: { firstName, report, stage, expiresAt }. */
  async phoneOpen(token) {
    try {
      const response = await this.api.get(`/sign-handoff/${encodeURIComponent(token)}`);
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'This signing link could not be opened');
    }
  }

  /** The typed short code, swapped for a token. */
  async phoneRedeemCode(code) {
    try {
      const response = await this.api.post('/sign-handoff/code', { code });
      return response.data?.data?.token;
    } catch (error) {
      throw toError(error, 'That code could not be used');
    }
  }

  async phoneSubmit(token, image) {
    try {
      await this.api.post(`/sign-handoff/${encodeURIComponent(token)}`, { image });
    } catch (error) {
      throw toError(error, 'Your signature could not be sent');
    }
  }
}

const signatureService = new SignatureService();
export default signatureService;
